import {
  CalendarDays,
  LayoutGrid,
  List as ListIcon,
  Pencil,
  Pin,
  PinOff,
  Search,
  Table as TableIcon,
  Trash2,
} from 'lucide-react';
import { useMemo, useState } from 'react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardFooter } from '@/components/ui/card';
import { FormSection } from '@/components/ui/form-section';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Textarea } from '@/components/ui/textarea';
import { ViewModeToggle, type ViewModeOption } from '@/components/ui/view-mode-toggle';
import type { ApiError } from '@/core/api';
import { useMembers } from '@/features/members';
import { useCreateNote, useDeleteNote, useNotes, useUpdateNote } from '../queries';
import { useNotesUi, type NotesViewMode } from '../store/viewPrefs';
import type { Note } from '../types';

// ---- View mode -------------------------------------------------------------

const VIEW_MODES: readonly ViewModeOption<NotesViewMode>[] = [
  { value: 'cards', label: 'Cards', icon: LayoutGrid },
  { value: 'list', label: 'List', icon: ListIcon },
  { value: 'table', label: 'Table', icon: TableIcon },
  { value: 'grouped', label: 'Grouped', icon: CalendarDays },
];

// ---- Author lookup ---------------------------------------------------------

function useAuthorNames() {
  const { data } = useMembers();
  return useMemo(() => {
    const map = new Map<number, string>();
    for (const m of data?.results ?? []) map.set(m.user.id, m.user.full_name);
    return map;
  }, [data]);
}

// ---- Composer --------------------------------------------------------------

function NoteComposer() {
  const [body, setBody] = useState('');
  const [pinOnCreate, setPinOnCreate] = useState(false);
  const create = useCreateNote();

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    const trimmed = body.trim();
    if (!trimmed) return;
    await create.mutateAsync({ body: trimmed, is_pinned: pinOnCreate });
    setBody('');
    setPinOnCreate(false);
  }

  return (
    <form onSubmit={submit}>
      <FormSection title="Compose" description="One thought at a time.">
        <Textarea
          rows={3}
          placeholder="Write a note…"
          value={body}
          onChange={(e) => setBody(e.target.value)}
          aria-label="New note"
        />
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2 text-sm">
            <Switch
              id="pin-on-create"
              checked={pinOnCreate}
              onCheckedChange={setPinOnCreate}
              aria-label="Pin on create"
            />
            <label
              htmlFor="pin-on-create"
              className="text-muted-foreground cursor-pointer"
            >
              <Pin className="mr-1 inline size-3.5 -translate-y-px" />
              Pin on create
            </label>
          </div>
          <Button type="submit" disabled={create.isPending || !body.trim()}>
            {create.isPending ? 'Adding…' : 'Add note'}
          </Button>
        </div>
      </FormSection>
    </form>
  );
}

// ---- Toolbar ---------------------------------------------------------------

interface ToolbarProps {
  view: NotesViewMode;
  onViewChange: (v: NotesViewMode) => void;
  search: string;
  onSearchChange: (s: string) => void;
  pinnedOnly: boolean;
  onPinnedOnlyChange: (v: boolean) => void;
}

function NotesToolbar({
  view, onViewChange, search, onSearchChange, pinnedOnly, onPinnedOnlyChange,
}: ToolbarProps) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-3">
      <div className="relative flex-1 sm:max-w-xs">
        <Search className="text-muted-foreground absolute top-2.5 left-2.5 size-4" />
        <Input
          className="pl-8"
          placeholder="Search notes"
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          aria-label="Search notes"
        />
      </div>

      <div className="flex items-center gap-2">
        <Button
          type="button"
          size="sm"
          variant={pinnedOnly ? 'default' : 'outline'}
          onClick={() => onPinnedOnlyChange(!pinnedOnly)}
          aria-pressed={pinnedOnly}
        >
          <Pin className="size-4" /> Pinned only
        </Button>

        <ViewModeToggle value={view} onChange={onViewChange} modes={VIEW_MODES} />
      </div>
    </div>
  );
}

// ---- Note actions + per-view components -----------------------------------

interface NoteActionsProps {
  note: Note;
  onEdit?: () => void;
}

function NoteActions({ note, onEdit }: NoteActionsProps) {
  const update = useUpdateNote();
  const remove = useDeleteNote();
  return (
    <div className="flex items-center gap-1">
      <Button
        size="icon"
        variant="ghost"
        aria-label={note.is_pinned ? 'Unpin note' : 'Pin note'}
        title={note.is_pinned ? 'Unpin' : 'Pin'}
        onClick={() =>
          update.mutate({ id: note.public_id, input: { is_pinned: !note.is_pinned } })
        }
      >
        {note.is_pinned ? <PinOff className="size-4" /> : <Pin className="size-4" />}
      </Button>
      {onEdit ? (
        <Button size="icon" variant="ghost" aria-label="Edit note" onClick={onEdit}>
          <Pencil className="size-4" />
        </Button>
      ) : null}
      <Button
        size="icon"
        variant="ghost"
        aria-label="Delete note"
        onClick={() => remove.mutate(note.public_id)}
        disabled={remove.isPending}
      >
        <Trash2 className="size-4" />
      </Button>
    </div>
  );
}

function NoteCard({ note, authorName }: { note: Note; authorName?: string }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(note.body);
  const update = useUpdateNote();

  async function saveEdit() {
    const trimmed = draft.trim();
    if (!trimmed || trimmed === note.body) {
      setEditing(false);
      setDraft(note.body);
      return;
    }
    await update.mutateAsync({ id: note.public_id, input: { body: trimmed } });
    setEditing(false);
  }

  return (
    <Card>
      <CardContent className="pt-6">
        {editing ? (
          <Textarea
            rows={3}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            aria-label="Edit note"
          />
        ) : (
          <p className="whitespace-pre-wrap text-sm">{note.body}</p>
        )}
      </CardContent>
      <CardFooter className="text-muted-foreground flex items-center justify-between text-xs">
        <span className="inline-flex items-center gap-2">
          {note.is_pinned ? <Pin className="size-3" /> : null}
          {authorName ? `${authorName} · ` : ''}
          {new Date(note.created).toLocaleDateString()}
        </span>
        {editing ? (
          <div className="flex items-center gap-1">
            <Button
              size="sm"
              variant="ghost"
              onClick={() => { setEditing(false); setDraft(note.body); }}
            >
              Cancel
            </Button>
            <Button size="sm" onClick={saveEdit} disabled={update.isPending}>
              Save
            </Button>
          </div>
        ) : (
          <NoteActions note={note} onEdit={() => setEditing(true)} />
        )}
      </CardFooter>
    </Card>
  );
}

function NoteRow({ note, authorName }: { note: Note; authorName?: string }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(note.body);
  const update = useUpdateNote();

  async function saveEdit() {
    const trimmed = draft.trim();
    if (!trimmed || trimmed === note.body) {
      setEditing(false);
      setDraft(note.body);
      return;
    }
    await update.mutateAsync({ id: note.public_id, input: { body: trimmed } });
    setEditing(false);
  }

  return (
    <li className="flex items-start justify-between gap-3 px-3 py-2">
      <div className="min-w-0 flex-1">
        {editing ? (
          <Textarea
            rows={2}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            aria-label="Edit note"
          />
        ) : (
          <p className="truncate text-sm">
            {note.is_pinned ? (
              <Pin className="text-muted-foreground mr-1 inline size-3 -translate-y-px" />
            ) : null}
            {note.body}
          </p>
        )}
        <p className="text-muted-foreground mt-0.5 text-xs">
          {authorName ? `${authorName} · ` : ''}
          {new Date(note.created).toLocaleDateString()}
        </p>
      </div>
      {editing ? (
        <div className="flex items-center gap-1">
          <Button
            size="sm"
            variant="ghost"
            onClick={() => { setEditing(false); setDraft(note.body); }}
          >
            Cancel
          </Button>
          <Button size="sm" onClick={saveEdit} disabled={update.isPending}>
            Save
          </Button>
        </div>
      ) : (
        <NoteActions note={note} onEdit={() => setEditing(true)} />
      )}
    </li>
  );
}

// ---- Grouping by recency --------------------------------------------------

const MS_DAY = 24 * 60 * 60 * 1000;
type Bucket = 'pinned' | 'today' | 'week' | 'month' | 'older';

function bucketFor(note: Note): Bucket {
  if (note.is_pinned) return 'pinned';
  const ageMs = Date.now() - new Date(note.created).getTime();
  if (ageMs < MS_DAY) return 'today';
  if (ageMs < 7 * MS_DAY) return 'week';
  if (ageMs < 30 * MS_DAY) return 'month';
  return 'older';
}

const BUCKET_LABELS: Record<Bucket, string> = {
  pinned: 'Pinned',
  today: 'Today',
  week: 'This week',
  month: 'Earlier this month',
  older: 'Older',
};
const BUCKET_ORDER: Bucket[] = ['pinned', 'today', 'week', 'month', 'older'];

// ---- Page -----------------------------------------------------------------

export function NotesListPage() {
  // Workspace Notes are personal — only the current user's own notes.
  const { data, isPending, isError, error } = useNotes({ mine: true });
  const authorNames = useAuthorNames();

  // Per-slice selectors keep this page from re-rendering when an unrelated
  // slice of the store changes (CLAUDE.md §2.3). The view-mode slice is the
  // only one persisted across reloads (see `viewPrefs.ts > partialize`).
  const view = useNotesUi((s) => s.view);
  const setView = useNotesUi((s) => s.setView);
  const search = useNotesUi((s) => s.search);
  const setSearch = useNotesUi((s) => s.setSearch);
  const pinnedOnly = useNotesUi((s) => s.pinnedOnly);
  const setPinnedOnly = useNotesUi((s) => s.setPinnedOnly);

  // Pinned first, then newest. Client-side filter on search + pinned-only.
  const notes = useMemo(() => {
    const q = search.trim().toLowerCase();
    const rows = (data?.results ?? [])
      .filter((n) => !pinnedOnly || n.is_pinned)
      .filter((n) => (q ? n.body.toLowerCase().includes(q) : true));
    rows.sort((a, b) => {
      if (a.is_pinned !== b.is_pinned) return a.is_pinned ? -1 : 1;
      return b.created.localeCompare(a.created);
    });
    return rows;
  }, [data, search, pinnedOnly]);

  return (
    <div className="max-w-3xl space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Notes</h1>
        <p className="text-muted-foreground text-sm">
          Quick workspace notes. Pin the ones that matter.
        </p>
      </div>

      <NoteComposer />

      <NotesToolbar
        view={view}
        onViewChange={setView}
        search={search}
        onSearchChange={setSearch}
        pinnedOnly={pinnedOnly}
        onPinnedOnlyChange={setPinnedOnly}
      />

      {isError ? (
        <p className="text-destructive text-sm">
          {(error as unknown as ApiError)?.message ?? 'Failed to load notes.'}
        </p>
      ) : isPending ? (
        <div className="space-y-3">
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-24 w-full" />
        </div>
      ) : notes.length === 0 ? (
        <p className="text-muted-foreground text-sm">
          {search || pinnedOnly
            ? 'No notes match this filter.'
            : 'No notes yet. Add your first above.'}
        </p>
      ) : view === 'cards' ? (
        <div className="space-y-3">
          {notes.map((note) => (
            <NoteCard
              key={note.public_id}
              note={note}
              authorName={note.author ? authorNames.get(note.author) : undefined}
            />
          ))}
        </div>
      ) : view === 'list' ? (
        <ul className="divide-y rounded-md border">
          {notes.map((note) => (
            <NoteRow
              key={note.public_id}
              note={note}
              authorName={note.author ? authorNames.get(note.author) : undefined}
            />
          ))}
        </ul>
      ) : view === 'table' ? (
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-10" aria-label="Pinned" />
                <TableHead>Note</TableHead>
                <TableHead className="hidden sm:table-cell">Author</TableHead>
                <TableHead className="hidden sm:table-cell">Created</TableHead>
                <TableHead className="w-28 text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {notes.map((note) => (
                <TableRow key={note.public_id}>
                  <TableCell>
                    {note.is_pinned ? (
                      <Pin className="text-muted-foreground size-3.5" />
                    ) : null}
                  </TableCell>
                  <TableCell className="max-w-md truncate text-sm">{note.body}</TableCell>
                  <TableCell className="text-muted-foreground hidden text-xs sm:table-cell">
                    {note.author ? authorNames.get(note.author) ?? '—' : '—'}
                  </TableCell>
                  <TableCell className="text-muted-foreground hidden text-xs sm:table-cell">
                    {new Date(note.created).toLocaleDateString()}
                  </TableCell>
                  <TableCell className="text-right">
                    <NoteActions note={note} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      ) : (
        // grouped
        <div className="space-y-6">
          {BUCKET_ORDER.map((bucket) => {
            const inBucket = notes.filter((n) => bucketFor(n) === bucket);
            if (inBucket.length === 0) return null;
            return (
              <section key={bucket} aria-labelledby={`group-${bucket}`}>
                <h2
                  id={`group-${bucket}`}
                  className="text-muted-foreground mb-2 flex items-center gap-2 text-xs font-semibold tracking-wider uppercase"
                >
                  {BUCKET_LABELS[bucket]}
                  <Badge variant="outline" className="text-[10px]">
                    {inBucket.length}
                  </Badge>
                </h2>
                <div className="space-y-3">
                  {inBucket.map((note) => (
                    <NoteCard
                      key={note.public_id}
                      note={note}
                      authorName={note.author ? authorNames.get(note.author) : undefined}
                    />
                  ))}
                </div>
              </section>
            );
          })}
        </div>
      )}
    </div>
  );
}
